import React, { useCallback, useEffect, useRef, useState } from "react";
import { Plus, Pencil, Trash2, GripVertical, Eye, EyeOff, Code2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { colors } from "@/lib/theme";
import { Sheet, PrimaryButton } from "@/components/peeps/ui";

// Renders a single custom HTML block inside a sandboxed, auto-resizing iframe.
// The sandbox (allow-scripts only, no same-origin) lets pasted <style>/<script>
// run for animations while staying fully isolated from the app's DOM/styles.
function HtmlFrame({ id, html }) {
  const ref = useRef(null);
  const [height, setHeight] = useState(80);

  const srcDoc = `<!DOCTYPE html><html><head><meta name="viewport" content="width=device-width, initial-scale=1">
<style>html,body{margin:0;padding:0;background:transparent;color:#F8F9FA;font-family:'Space Grotesk',system-ui,sans-serif;overflow-x:hidden;}</style>
</head><body>${html}
<script>(function(){function h(){return Math.max(document.documentElement.scrollHeight||0,document.body.scrollHeight||0);}function post(){parent.postMessage({__phg_h:h(),__phg_id:${JSON.stringify(id)}},"*");}window.addEventListener("load",post);[100,400,900,1800,3000].forEach(function(t){setTimeout(post,t);});try{if(window.ResizeObserver){new ResizeObserver(post).observe(document.body);}}catch(e){}setInterval(post,2000);}());<\/script>
</body></html>`;

  useEffect(() => {
    const onMsg = (e) => {
      const d = e.data;
      if (d && d.__phg_id === id && typeof d.__phg_h === "number" && d.__phg_h > 0) {
        setHeight(Math.ceil(d.__phg_h));
      }
    };
    window.addEventListener("message", onMsg);
    return () => window.removeEventListener("message", onMsg);
  }, [id]);

  return (
    <iframe
      ref={ref}
      title={`section-${id}`}
      data-testid={`section-frame-${id}`}
      sandbox="allow-scripts allow-top-navigation-by-user-activation allow-popups"
      srcDoc={srcDoc}
      scrolling="no"
      style={{ width: "100%", height, border: "none", display: "block", background: "transparent" }}
    />
  );
}

export function CustomSections({ page }) {
  const { user } = useAuth();
  const isAdmin = !!user?.is_app_admin;
  const [sections, setSections] = useState([]);
  const [editorOpen, setEditorOpen] = useState(false);
  const [draft, setDraft] = useState({ id: null, html: "" });
  const [saving, setSaving] = useState(false);
  const dragIndex = useRef(null);

  const load = useCallback(async () => {
    try {
      setSections(await api.listSections(page));
    } catch {}
  }, [page]);

  useEffect(() => { load(); }, [load]);

  const visible = isAdmin ? sections : sections.filter((s) => s.enabled);
  if (!isAdmin && visible.length === 0) return null;

  const openNew = () => { setDraft({ id: null, html: "" }); setEditorOpen(true); };
  const openEdit = (s) => { setDraft({ id: s.id, html: s.html }); setEditorOpen(true); };

  const save = async () => {
    if (!draft.html.trim()) return;
    setSaving(true);
    try {
      if (draft.id) {
        await api.updateSection(draft.id, { html: draft.html });
        toast("Section updated");
      } else {
        await api.createSection(page, draft.html);
        toast("Section added");
      }
      setEditorOpen(false);
      await load();
    } catch (e) { toast(e.message || "Couldn't save section"); }
    finally { setSaving(false); }
  };

  const remove = async (s) => {
    try { await api.deleteSection(s.id); await load(); toast("Section removed"); }
    catch (e) { toast(e.message || "Couldn't remove"); }
  };

  const toggleEnabled = async (s) => {
    try { await api.updateSection(s.id, { enabled: !s.enabled }); await load(); }
    catch (e) { toast(e.message || "Couldn't update"); }
  };

  const onDrop = async (toIdx) => {
    const from = dragIndex.current;
    dragIndex.current = null;
    if (from === null || from === toIdx) return;
    const next = [...sections];
    const [moved] = next.splice(from, 1);
    next.splice(toIdx, 0, moved);
    setSections(next);
    try { await api.reorderSections(page, next.map((s) => s.id)); }
    catch (e) { toast(e.message || "Couldn't reorder"); await load(); }
  };

  return (
    <div data-testid={`custom-sections-${page}`} className="flex flex-col gap-4 mt-6">
      {visible.map((s, idx) => (
        <div
          key={s.id}
          data-testid={`custom-section-${s.id}`}
          draggable={isAdmin}
          onDragStart={() => { if (isAdmin) dragIndex.current = idx; }}
          onDragOver={(e) => { if (isAdmin) e.preventDefault(); }}
          onDrop={(e) => { if (isAdmin) { e.preventDefault(); onDrop(idx); } }}
          className="rounded-2xl overflow-hidden"
          style={isAdmin ? { border: `1px dashed ${colors.border}`, opacity: s.enabled ? 1 : 0.45 } : {}}
        >
          {isAdmin && (
            <div className="flex items-center gap-2 px-3 py-2" style={{ background: colors.surfaceSecondary, borderBottom: `1px solid ${colors.border}` }}>
              <span data-testid={`section-drag-${s.id}`} className="cursor-grab active:cursor-grabbing" title="Drag to reorder">
                <GripVertical size={16} color={colors.onSurfaceTertiary} />
              </span>
              <span className="font-mono2 text-[11px]" style={{ color: colors.faint }}>HTML #{idx + 1}</span>
              <div className="ml-auto flex items-center gap-1">
                <button data-testid={`section-toggle-${s.id}`} onClick={() => toggleEnabled(s)} className="p-1.5 rounded-md" style={{ background: colors.surfaceTertiary }} title={s.enabled ? "Hide" : "Show"}>
                  {s.enabled ? <Eye size={14} color={colors.onSurfaceTertiary} /> : <EyeOff size={14} color={colors.muted} />}
                </button>
                <button data-testid={`section-edit-${s.id}`} onClick={() => openEdit(s)} className="p-1.5 rounded-md" style={{ background: colors.surfaceTertiary }} title="Edit HTML">
                  <Pencil size={14} color={colors.brandSecondary} />
                </button>
                <button data-testid={`section-delete-${s.id}`} onClick={() => remove(s)} className="p-1.5 rounded-md" style={{ background: colors.surfaceTertiary }} title="Delete">
                  <Trash2 size={14} color={colors.error} />
                </button>
              </div>
            </div>
          )}
          <HtmlFrame id={s.id} html={s.html} />
        </div>
      ))}

      {isAdmin && (
        <button
          data-testid={`add-section-${page}`}
          onClick={openNew}
          className="flex items-center justify-center gap-2 w-full rounded-2xl py-3.5"
          style={{ border: `1px dashed ${colors.border}`, background: colors.surfaceSecondary }}
        >
          <Code2 size={16} color={colors.brandSecondary} />
          <span className="font-display font-medium text-[13.5px]" style={{ color: colors.brandSecondary }}>Add custom HTML section</span>
          <Plus size={15} color={colors.brandSecondary} />
        </button>
      )}

      {editorOpen && (
        <Sheet visible={editorOpen} onClose={() => setEditorOpen(false)} title={draft.id ? "Edit HTML section" : "Add HTML section"} testID="section-editor-sheet">
          <p className="font-display text-[12.5px] mb-2" style={{ color: colors.onSurfaceTertiary }}>
            Paste any HTML. Styles and scripts run inside an isolated frame, so animations work without affecting the rest of the page.
          </p>
          <textarea
            data-testid="section-html-input"
            value={draft.html}
            onChange={(e) => setDraft({ ...draft, html: e.target.value })}
            rows={12}
            spellCheck={false}
            placeholder="<div>…your HTML…</div>"
            className="w-full rounded-xl p-3 font-mono2 text-[12.5px] resize-y mb-4"
            style={{ background: colors.surfaceTertiary, border: `1px solid ${colors.border}`, color: colors.onSurface, minHeight: 220 }}
          />
          <PrimaryButton testID="section-save-button" label={draft.id ? "Save section" : "Add section"} onClick={save} disabled={!draft.html.trim()} loading={saving} />
        </Sheet>
      )}
    </div>
  );
}
